// Boundary between `QueryFootnoteFieldsFragment` (BE read shape) and the form
// shape consumed by `FootnoteSettingsPanel`.
//
// - `toFootnoteSettingsValues` (read): seeds the form when the panel opens.
// - `applyFootnoteSettingsToRaw` (write): folds the form values back into the
//   raw fragment so `toQueryFootnoteInputs` (in `footnotesMapper.ts`) picks
//   them up on the takeover's buffered Save.
//
//
// The footnote symbol enum maps are shared with `QuerySettingsPanel` so the
// per-footnote override and the query default resolve identically.

import { toRules, toRuleSetType } from "../../../../shared/RulesSection/mappers";
import { EMPTY_FOOTNOTE_LINK } from "../footnotesMapper";
import {
  FOOTNOTE_SYMBOL_FROM_BE,
  FOOTNOTE_SYMBOL_TO_BE,
} from "../QuerySettingsPanel/settingsMapper";
import { DEFAULT_FOOTNOTE_SETTINGS_VALUES } from "./constants";
import type { FootnoteSettingsValues } from "./types";
import type { FootnoteSymbolOption } from "../QuerySettingsPanel/types";
import type { QueryFootnoteFieldsFragment } from "~/graphql/supergraphTypes";

/** Backend `QueryFootnoteFieldsFragment` → form values.
 *
 * `queryFootnoteSymbol` is the query-level default style; it seeds the
 * symbol select so flipping the override toggle on doesn't reset to an
 * unrelated style. */
export const toFootnoteSettingsValues = (
  raw: QueryFootnoteFieldsFragment,
  queryFootnoteSymbol: FootnoteSymbolOption = DEFAULT_FOOTNOTE_SETTINGS_VALUES.footnoteSymbol,
): FootnoteSettingsValues => {
  return {
    footnoteKind: raw.footnoteKind,
    name: raw.name,
    text: raw.text ?? "",
    customFieldId: raw.customFieldId != null ? String(raw.customFieldId) : "",
    overrideFootnoteSymbol: raw.overrideFootnoteSymbol,
    footnoteSymbol: raw.footnoteSymbolStyle
      ? FOOTNOTE_SYMBOL_FROM_BE[raw.footnoteSymbolStyle]
      : queryFootnoteSymbol,
    suppressionRules: toRules(raw.suppressionRules),
  };
};

/** Form values → updated raw fragment. The takeover's Save then runs the
 * existing `toQueryFootnoteInputs` write path against the mutated rows.
 *
 * Symbols are placed manually via the `{footnotesymbol.<name>}` merge field,
 * so every footnote is written unlinked — any legacy `linked*` reference is
 * cleared on save. */
export const applyFootnoteSettingsToRaw = (
  raw: QueryFootnoteFieldsFragment,
  values: FootnoteSettingsValues,
): QueryFootnoteFieldsFragment => {
  const isFreeform = values.footnoteKind === "FREEFORM_TEXT";
  const customFieldId = isFreeform ? null : values.customFieldId || null;
  return {
    ...raw,
    footnoteKind: values.footnoteKind,
    name: values.name.trim(),
    // `symbolMergeField` rides along untouched from `raw`. The server keys a
    // footnote by the slug inside it, not by the name, so carrying it through
    // a rename is what keeps the token bound to this footnote.
    // Freeform body and custom-field reference are mutually exclusive: a
    // custom-field footnote derives its text from the linked custom field, so
    // its `text` must be null — the backend rejects a CUSTOM_FIELD footnote
    // that sets text (even an empty string).
    text: isFreeform ? values.text : null,
    customFieldId,
    ...EMPTY_FOOTNOTE_LINK,
    overrideFootnoteSymbol: values.overrideFootnoteSymbol,
    footnoteSymbolStyle: values.overrideFootnoteSymbol
      ? FOOTNOTE_SYMBOL_TO_BE[values.footnoteSymbol]
      : null,
    suppressionRules: toRuleSetType(values.suppressionRules),
  };
};
